import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { checkout } from "../lib/api";
import { toast } from "sonner";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, discount, setDiscount } = useCart();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const applyCode = () => {
    if (!code) return;
    setDiscount(code.trim().toUpperCase());
    toast.success("Discount code applied at checkout");
  };

  const onCheckout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, lookup_key: i.lookup_key, size: i.size, quantity: i.quantity })),
        origin_url: window.location.origin,
        discount_code: discount || null,
      };
      const r = await checkout(payload);
      window.location.href = r.checkout_url;
    } catch {
      toast.error("Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div className="fixed inset-0 bg-black/60 z-[60]"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} data-testid="cart-overlay" />
          <motion.aside
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-bg z-[70] border-l border-line flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-line">
              <span className="font-mono text-xs uppercase tracking-[0.2em]">Your Bag ({items.length})</span>
              <button onClick={() => setOpen(false)} data-testid="cart-close-btn"><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar">
              {items.length === 0 ? (
                <div className="p-8 text-graytx text-sm" data-testid="cart-empty">Your bag is empty. The drop awaits.</div>
              ) : items.map((i) => (
                <div key={i.product_id + i.size} className="flex gap-4 p-5 border-b border-line" data-testid={`cart-item-${i.product_id}`}>
                  <img src={i.image} alt={i.name} className="w-20 h-24 object-cover bg-charcoal" />
                  <div className="flex-1">
                    <div className="flex justify-between items-start">
                      <p className="font-display font-semibold text-sm leading-tight">{i.name}</p>
                      <button onClick={() => removeItem(i.product_id, i.size)} data-testid={`cart-remove-${i.product_id}`} className="text-graytx hover:text-cs-red transition-colors duration-200"><Trash2 size={15} /></button>
                    </div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-graytx mt-1">Size {i.size}{i.preorder ? " · Pre-order" : ""}</p>
                    <div className="flex justify-between items-center mt-3">
                      <div className="flex items-center border border-line">
                        <button onClick={() => updateQty(i.product_id, i.size, i.quantity - 1)} className="px-2 py-1" data-testid={`cart-dec-${i.product_id}`}><Minus size={13} /></button>
                        <span className="px-3 text-sm tabular-nums">{i.quantity}</span>
                        <button onClick={() => updateQty(i.product_id, i.size, i.quantity + 1)} className="px-2 py-1" data-testid={`cart-inc-${i.product_id}`}><Plus size={13} /></button>
                      </div>
                      <span className="font-mono text-sm">${(i.price * i.quantity).toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {items.length > 0 && (
              <div className="border-t border-line p-6 space-y-4">
                <div className="flex gap-2">
                  <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code"
                    data-testid="cart-code-input"
                    className="flex-1 bg-transparent border border-line px-3 py-2 text-sm outline-none focus:border-cream transition-colors duration-200 placeholder:text-graytx" />
                  <button onClick={applyCode} data-testid="cart-apply-code" className="font-mono text-[10px] uppercase tracking-[0.18em] border border-line px-4 hover:bg-charcoal transition-colors duration-200">Apply</button>
                </div>
                {discount && <p className="font-mono text-[10px] uppercase tracking-widest text-cs-green" data-testid="cart-discount-applied">Code {discount} · 15% off applied</p>}
                <div className="flex justify-between font-mono text-sm">
                  <span className="text-graytx uppercase tracking-widest text-[11px]">Subtotal</span>
                  <span>${subtotal.toFixed(0)}</span>
                </div>
                <button onClick={onCheckout} disabled={loading} data-testid="cart-checkout-btn"
                  className="w-full bg-cream text-bg font-mono text-[12px] uppercase tracking-[0.2em] py-4 hover:bg-cs-yellow disabled:opacity-60 transition-colors duration-200">
                  {loading ? "Redirecting…" : "Checkout"}
                </button>
                <p className="text-[10px] text-graytx text-center">Secure payment via Stripe · Taxes & shipping at checkout</p>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
